import { useEffect, useState } from 'react'
import { buildFaviconHref } from '../icons/favicon.js'
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import type { Client } from '@shared/client.js'
import { ProtocolError } from '@shared/protocol.js'
import type { Board, Column } from '@shared/types.js'
import { useBoard } from '../queries.js'
import { EmptyState } from './EmptyState.js'
import { AddColumnButton } from './AddColumnButton.js'
import { BoardDndContext } from '../dnd/BoardDndContext.js'
import { SortableColumn } from '../dnd/SortableColumn.js'
import { encodeColumnId } from '../dnd/cardId.js'
import { useActiveBoard } from '../contexts/ActiveBoardContext.js'
import { BoardFocusProvider } from '../contexts/BoardFocusContext.js'
import { BoardFilterProvider, useBoardFilter } from '../contexts/BoardFilterContext.js'
import { BoardsGrid } from './BoardsGrid.js'
import { BoardListView } from './BoardListView.js'
import { BoardCalendarView } from './BoardCalendarView.js'
import { BoardHeader } from './BoardHeader.js'
import { FilterSidePanel, FilterDrawer } from './FilterPopover.js'
import { FocusedColumnProvider, useFocusedColumn } from '../contexts/FocusedColumnContext.js'
import { FocusExitBar } from './FocusExitBar.js'
import { useBoardSettings } from '../queries/useBoardSettings.js'
import { useAvailableTags, useTagCounts } from '../queries/useAvailableTags.js'
import { useBoardSettingsContext } from '../contexts/BoardSettingsContext.js'
import { useMediaQuery } from '../hooks/useMediaQuery.js'

function BoardColumns({
  data,
  active,
  columns,
}: {
  data: NonNullable<ReturnType<typeof useBoard>['data']>
  active: string
  columns: Column[]
}): JSX.Element {
  const { focused } = useFocusedColumn()
  const names = columns.map((c) => c.name)
  const columnIds = names.map(encodeColumnId)

  const visibleColumns = focused !== null ? columns.filter((c) => c.name === focused) : columns

  const columnsRow = (
    <div className="flex flex-1 gap-4 overflow-x-auto p-4">
      {visibleColumns.map((col) => {
        const i = columns.indexOf(col)
        return (
          <SortableColumn
            key={`${col.name}-${i}`}
            column={col}
            colIdx={i}
            allColumnNames={names}
            boardId={active}
            collapsed={data.list_collapse?.[i] ?? false}
            isFocusMode={focused !== null}
          />
        )
      })}
      {focused === null && <AddColumnButton boardId={active} />}
    </div>
  )

  return (
    <div className="flex flex-1 flex-col">
      {focused !== null && (
        <div className="px-4 pt-4">
          <FocusExitBar />
        </div>
      )}
      {focused !== null ? (
        columnsRow
      ) : (
        <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
          {columnsRow}
        </SortableContext>
      )}
    </div>
  )
}

function FilterPanels({
  data,
  availableTags,
  filterOpen,
  setFilterOpen,
  filterFocus,
  setFilterFocus,
  isDesktop,
}: {
  data: Board
  availableTags: string[]
  filterOpen: boolean
  setFilterOpen: (v: boolean) => void
  filterFocus: 'search' | null
  setFilterFocus: (v: 'search' | null) => void
  isDesktop: boolean
}): JSX.Element {
  const { filter } = useBoardFilter()
  const tagCounts = useTagCounts(data, filter.hideCompleted)
  return (
    <>
      {isDesktop && (
        <FilterSidePanel
          availableTags={availableTags}
          tagCounts={tagCounts}
          open={filterOpen}
          onOpenChange={setFilterOpen}
        />
      )}
      {!isDesktop && (
        <FilterDrawer
          availableTags={availableTags}
          tagCounts={tagCounts}
          open={filterOpen}
          onOpenChange={(next) => {
            setFilterOpen(next)
            if (!next) setFilterFocus(null)
          }}
          initialFocus={filterFocus}
        />
      )}
    </>
  )
}

export function BoardView({ client, onToggleSidebar }: { client: Client; onToggleSidebar: () => void }): JSX.Element {
  const { active, setActive } = useActiveBoard()
  const { data, isLoading, error } = useBoard(active)
  const settings = useBoardSettings(active)
  const { openSettings } = useBoardSettingsContext()
  const availableTags = useAvailableTags(data)
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const [filterOpen, setFilterOpen] = useState(false)
  const [filterFocus, setFilterFocus] = useState<'search' | null>(null)
  const toggleFilter = (): void => {
    setFilterFocus(null)
    setFilterOpen((prev) => !prev)
  }
  const openSearch = (): void => {
    setFilterFocus('search')
    setFilterOpen(true)
  }

  useEffect(() => {
    if (!active) return
    void client.subscribe(active)
    return () => {
      void client.unsubscribe(active)
    }
  }, [active, client])

  useEffect(() => {
    if (error instanceof ProtocolError && error.code === 'NOT_FOUND') {
      setActive(null)
    }
  }, [error, setActive])

  useEffect(() => {
    const title = data?.name ? `${data.name} — LiveBoard` : 'LiveBoard'
    const faviconHref = buildFaviconHref(data?.icon, data?.icon_color)
    client.emit('title.changed', { title, faviconHref })
  }, [client, data?.name, data?.icon, data?.icon_color])

  if (!active) return <BoardsGrid />
  if (isLoading) return <EmptyState title="Loading…" />
  if (error) return <EmptyState title="Failed to load board" detail={String(error)} />
  if (!data) return <EmptyState title="Board not found" />

  const columns = data.columns ?? []
  if (columns.length === 0) {
    return (
      <div className="flex h-full gap-4 overflow-x-auto p-4">
        <AddColumnButton boardId={active} />
      </div>
    )
  }

  const view =
    settings.view_mode === 'calendar' ? (
      <BoardCalendarView data={data} active={active} columns={columns} />
    ) : settings.view_mode === 'list' ? (
      <BoardListView data={data} active={active} columns={columns} />
    ) : (
      <BoardColumns data={data} active={active} columns={columns} />
    )

  return (
    <BoardFilterProvider boardId={active} availableTags={availableTags}>
      <BoardFocusProvider columns={columns}>
        <FocusedColumnProvider columns={columns}>
          <BoardDndContext boardId={active}>
            <div className="flex h-full flex-col">
              <BoardHeader
                data={data}
                onToggleSidebar={onToggleSidebar}
                onOpenSettings={openSettings}
                filterOpen={filterOpen}
                onOpenFilter={toggleFilter}
                onOpenSearch={openSearch}
              />
              <div className="flex min-h-0 flex-1">
                <div className="flex min-w-0 flex-1 flex-col">{view}</div>
                <FilterPanels
                  data={data}
                  availableTags={availableTags}
                  filterOpen={filterOpen}
                  setFilterOpen={setFilterOpen}
                  filterFocus={filterFocus}
                  setFilterFocus={setFilterFocus}
                  isDesktop={isDesktop}
                />
              </div>
            </div>
          </BoardDndContext>
        </FocusedColumnProvider>
      </BoardFocusProvider>
    </BoardFilterProvider>
  )
}
